import { UploadedTempFile } from '@/api/upload-temp-file';
import { Bleed, BlockStack, Box, Card, ProgressBar, Text } from '@shopify/polaris';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Image } from '@/components/Image';

export type MediaAssetUploadProgressCardProps = {
  uploadedTempFile?: UploadedTempFile;
  progress: number;
  file: File;
  size?: 'medium' | 'large' | 'small';
  aspectRatio?: 'square' | 'portrait';
};

export const MediaAssetUploadProgressCard = ({
  progress,
  file,
  uploadedTempFile,
  size = 'large',
  aspectRatio = 'portrait',
}: MediaAssetUploadProgressCardProps) => {
  const uploadFileSrc = useMemo(() => {
    if (uploadedTempFile?.publicUrl) return uploadedTempFile.publicUrl;
    return window.URL.createObjectURL(file);
  }, [uploadedTempFile?.publicUrl, file]);

  const isVideo = file.type.startsWith('video/');

  return (
    <div>
      <Card>
        <Bleed marginInline="400" marginBlock="400">
          <div
            className={cn(
              'relative flex flex-col justify-between overflow-hidden',
              size === 'large' && 'h-[310px]',
              size === 'medium' && 'h-[205px]',
              size === 'small' && 'h-[220px]',
              aspectRatio === 'square' ? 'aspect-square' : 'aspect-[9/16]'
            )}
          >
            <div className="relative h-full w-full">
              {isVideo ? (
                <video src={uploadFileSrc} className="h-full w-full object-cover" controls />
              ) : (
                <Image src={uploadFileSrc} alt={file.name} fill objectFit="cover" size={size} />
              )}
            </div>
          </div>

          <Box background="bg-surface-secondary" padding="400">
            <BlockStack gap="200">
              {!uploadedTempFile && <ProgressBar progress={progress} tone="primary" size="small" />}

              <Text as="span" variant="bodyMd" fontWeight="semibold" truncate>
                {file.name}
              </Text>
            </BlockStack>
          </Box>
        </Bleed>
      </Card>
    </div>
  );
};
